import { noOp } from 'common/Constants';
import { formatGqlErrors } from 'common/GqlUtilities';
import { decodeDonationConfig } from 'common/neighbors-feeding-neighbors/DonationConfig';
import { useCallback, useEffect } from 'react';
import { GlobalInitialStateDocument, GlobalInitialStateQuery } from 'src/gqlReactTypings.generated.d';
import { useGqlClient } from 'src/shared/useGqlClient';
import { Store } from 'src/store';
import { setAvailableAddOns } from 'src/store/cart/actions';
import { useMealPlanForUserStoreSelectors } from 'src/ztore/wss/meal-plan-for-user-store';
import { getToken } from '../shared/storage/Storage';
import { useCachedZipCode } from '../user-preferences/useCachedZipCode';
import { useGlobalReducer } from './shefGlobalState';

// TODO: Move configs / other things we want done only once into this file
export const useFetchGlobalState = ({ dispatch }: { dispatch: Store['dispatch'] }) => {
  const { reduce: globalReduce } = useGlobalReducer();
  const userIsLoggedIn = Boolean(getToken());
  const GqlClient = useGqlClient();
  const [cachedZipCode, setCachedZipCode] = useCachedZipCode();
  const { setMealPlanForUser } = useMealPlanForUserStoreSelectors();

  // Return a promise that can be used to know when fetch is done.
  let promiseResolve: (value?: any) => void = useCallback(noOp, []);
  const fetchPromise = new Promise((resolve) => {
    promiseResolve = resolve;
  });

  useEffect(() => {
    globalReduce((state) => ({ ...state, loading: true }));

    GqlClient.query<GlobalInitialStateQuery>({
      query: GlobalInitialStateDocument,
      variables: { zipCode: cachedZipCode },
      fetchPolicy: 'network-only',
    })
      .then(({ data, errors }) => {
        if (errors?.length) {
          console.error(formatGqlErrors(errors));
          return;
        }

        globalReduce((state) => ({
          ...state,
          errors,
          loaded: true,
          currentUser: data.me ?? null,
          userRegionId: data.zipCode?.regionId ?? null,
          userRegionName: data.zipCode?.region?.name ?? null,
          region: data.zipCode?.region ?? null,
          timeZone: data.zipCode?.timeZone ?? null,
          minimumAmountForDiscountedShipping: data.zipCode?.minimumAmountForDiscountedShipping ?? null,
          discountedDeliveryFee: data.zipCode?.discountedDeliveryFee ?? null,
          deliveryFee: data.zipCode?.deliveryFee ?? null,
          configs: {
            ...state.configs,
            shefRatingEnabled: data.shefRatingEnabled,
            appleSignInOnWebEnabled: data.appleSignInOnWebEnabled,
            upsellsEnabled: data.upsellsEnabled,
            apiSyncEnabled: data.apiSyncEnabled,
            profilePaymentMethodsEnabled: data.profilePaymentMethodsEnabled,
            profileDeliveryAddressesEnabled: data.profileDeliveryAddressesEnabled,
            rateRecentOrderPopupEnabled: data.rateRecentOrderPopupEnabled,
            giftCardPurchaseEnabled: data.giftCardPurchaseEnabled,
            holidayMenusEnabled: data.holidayMenusEnabled,
            shefPlatformUsageQuestionnaire: data.shefPlatformUsageQuestionnaire,
            dishwasher: data.dishwasher,
            shefDashboardInsights: data.shefDashboardInsights,
            shefInsightsReportEnabled: data.shefInsightsReportEnabled,
            requestAppReviewsEnabled: data.requestAppReviewsEnabled,
            shefMenuChecklistEnabled: data.shefMenuChecklistEnabled,
            shefPostsEnabled: data.shefPostsEnabled,
            enableAdminPages: !data.disableAdminPages,
            chatEnabled: data.chatEnabled,
            dishFirstNav: data.dishFirstNav,
            shefApplicationAlwaysEnableContinueInformationSessionVideo:
              data.shefApplicationAlwaysEnableContinueInformationSessionVideo,
            consumerDeleteUserEnabled: data.consumerDeleteUserEnabled,
            shefGrowYourBusinessEnabled: data.shefGrowYourBusinessEnabled,
            shefSideCustomizations: data.shefSideCustomizations,
            fringeOverrideEnabled: data.fringeOverrideEnabled,
            bulkDiscountEnabled: data.bulkDiscountEnabled,
            chatSupportKeywords: JSON.parse(data.chatSupportKeywords ?? ''),
            chatFeedbackKeywords: JSON.parse(data.chatFeedbackKeywords ?? ''),
            chatCustomizationKeywords: JSON.parse(data.chatCustomizationKeywords ?? ''),
            chatBusinessRiskKeywords: JSON.parse(data.chatBusinessRiskKeywords ?? ''),
            upfrontDeliveryFeeEnabled: data.zipCode?.upfrontDeliveryFeeEnabled ?? false,
            mealPlansEnabled: data.mealPlansEnabled,
            mealPlansAllergensEnabled: data.mealPlansAllergensEnabled,
            mealPlansBrowseAsGuest: data.mealPlansBrowseAsGuest,
            showManageMealPlans: data.showManageMealPlans,
            reusablePackagingEnabled: data.reusablePackagingEnabled,
            shefRosterEnabled: data.shefRosterEnabled,
            exploreWeeklyShefServiceTabsEnabled: data.exploreWeeklyShefServiceTabsEnabled,
            mealPlanDiscountFieldEnabled: data.mealPlanDiscountFieldEnabled,
            forYouPageEnabled: data.forYouPageEnabled,
            favoritesV2Enabled: data.favoritesV2Enabled,
            mealPlanDeliveryDayStartOverride: JSON.parse(data.mealPlanDeliveryDayStartOverride ?? '{}'),
            shefCompositeScoreEnabled: data.shefCompositeScoreEnabled,
            foodItemCompositeScoreEnabled: data.foodItemCompositeScoreEnabled,
            mealPlanAddOnsEnabled: data.mealPlanAddOnsEnabled,
            duplicateDishEnabled: data.duplicateDishEnabled,
            nativeShefApplicationQuestionnaire: data.nativeShefApplicationQuestionnaire,
            shefOnboardingRedesign: data.shefOnboardingRedesign,
            shefProfileRedesign: data.shefProfileRedesign,
            newExploreWssNonSubTabEnabled: data.newExploreWssNonSubTabEnabled,
            signUpModalGatingDsEnabled: data.signUpModalGatingDsEnabled,
            newReferralValuesEnabled: data.newReferralValuesEnabled,
            mealPlanGiftCardsEnabled: data.mealPlanGiftCardsEnabled,
            mealPlanCheckoutAddOnsEnabled: data.mealPlanCheckoutAddOnsEnabled,
            reusablePackagingCleaningFeeEnabled: data.reusablePackagingCleaningFeeEnabled,
            merchandiseTagsEnabled: data.merchandiseTagsEnabled,
            mealPlanCheckoutRevampV1Enabled: data.mealPlanCheckoutRevampV1Enabled,
            foodItemMacrosEnabled: data.foodItemMacrosEnabled,
            shefPayoutImprovementsEnabled: data.shefPayoutImprovementsEnabled,
            shefPayoutTiersEnabled: data.shefPayoutTiersEnabled,
            shefPayoutTierSurfacesEnabled: data.shefPayoutTierSurfacesEnabled,
            sidesPairingPageEnabled: data.sidesPairingPageEnabled,
            lunchCategoriesEnabled: data.lunchCategoriesEnabled,
            mealPlanSearchEnabled: data.mealPlanSearchEnabled,
            mealPlanSetupIntentEnabled: data.mealPlanSetupIntentEnabled,
            shefMerchLandingEnabled: data.shefMerchLandingEnabled,
            variableServiceFeeEnabled: data.variableServiceFeeEnabled,
            marketplaceVariableServiceFeeEnabled: data.marketplaceVariableServiceFeeEnabled,
            dynamicShefSortingEnabled: data.dynamicShefSortingEnabled,
            wssOrganicTagsEnabled: data.wssOrganicTagsEnabled,
            shefVarietyToolingEnabled: data.shefVarietyToolingEnabled,
            expressCheckoutV1Enabled: data.expressCheckoutV1Enabled,
            inlineStripeLinkEnabled: data.inlineStripeLinkEnabled,
            ccpaConsentBannerEnabled: data.ccpaConsentBannerEnabled,
            shefExtraAvailabilityEnabled: data.shefExtraAvailabilityEnabled,
            lowCalHighProteinCollectionsEnabled: data.lowCalHighProteinCollectionsEnabled,
            lowCalHighProteinMacroThresholdEnabled: data.lowCalHighProteinMacroThresholdEnabled,
            shefSeasonalToolingEnabled: data.shefSeasonalToolingEnabled,
            mealPlanPaymentElementOnAccountEnabled: data.mealPlanPaymentElementOnAccountEnabled,
            wssCuisineCarouselEnabledInApp: data.wssCuisineCarouselEnabledInApp,
            wssCuisineCarouselV3Enabled: data.wssCuisineCarouselV3Enabled,
            leadCaptureModalEnabled: data.leadCaptureModalEnabled,
            wssFlexibleServingsV2Enabled: data.wssFlexibleServingsV2Enabled,
            wssFlexibleServingsV3Enabled: data.wssFlexibleServingsV3Enabled,
            flexServingsPaymentEnabled: data.flexServingsPaymentEnabled,
            reusablePackagingBayAreaEnabled: data.reusablePackagingBayAreaEnabled,
            reusablePackagingNyEnabled: data.reusablePackagingNyEnabled,
            reusablePackagingLaEnabled: data.reusablePackagingLaEnabled,
            reusablePackagingSeaEnabled: data.reusablePackagingSeaEnabled,
            wssReusableOptInEnabled: data.wssReusableOptInEnabled,
            wssSteelConsolidationEnabled: data.wssSteelConsolidationEnabled,
            lpDishStoriesEnabled: data.lpDishStoriesEnabled,
            lpFullBleedVideoEnabled: data.lpFullBleedVideoEnabled,
            lpHowItWorksEnabled: data.lpHowItWorksEnabled,
            intakeHouseholdConsolidationV2Enabled: data.intakeHouseholdConsolidationV2Enabled,
            intakeOptimizationEnabled: data.intakeOptimizationEnabled,
            intakeSettingsBeforeCalendarEnabled: data.intakeSettingsBeforeCalendarEnabled,
            defaultPlanUpdateEnabled: data.defaultPlanUpdateEnabled,
            tablerunEnabled: data.tablerunEnabled,
            sidesPageSearchEnabled: data.sidesPageSearchEnabled,
            sidesCategoryCarouselEnabled: data.sidesCategoryCarouselEnabled,
            signUpGateRedesignEnabled: data.signUpGateRedesignEnabled,
            foodItemValidationEnabled: data.foodItemValidationEnabled,
            foodItemValidationPreviewEnabled: data.foodItemValidationPreviewEnabled,
            foodItemValidationShefFormEnabled: data.foodItemValidationShefFormEnabled,
            foodItemValidationIssuesPageEnabled: data.foodItemValidationIssuesPageEnabled,
            wssDessertProposalsEnabled: data.wssDessertProposalsEnabled,
            hideHomepageOneTimeTxEnabled: data.hideHomepageOneTimeTxEnabled,
            mealPlanTippingEnabled: data.mealPlanTippingEnabled,
            mealPlanRecurringTipEnabled: data.mealPlanRecurringTipEnabled,
            neighborsFeedingNeighborsEnabled: data.neighborsFeedingNeighborsEnabled,
            neighborsFeedingNeighborsMatchActive: data.neighborsFeedingNeighborsMatchActive,
            neighborsFeedingNeighborsConfig: decodeDonationConfig(data.neighborsFeedingNeighborsConfig),
            neighborsFeedingNeighborsMenuLinkEnabled: data.neighborsFeedingNeighborsMenuLinkEnabled,
            hubFlowV2ShefPackoutEnabled: data.hubFlowV2ShefPackoutEnabled,
          },
        }));

        /**
         * Beginning of Zustand store updates
         */
        const mealPlan = data?.getActiveMealPlanForUser;
        setMealPlanForUser(mealPlan ?? undefined);

        // If an existing user signs in from a new browser, let's update the browser cache
        // (unless they've overwritten this locally)
        if (!cachedZipCode && data.me?.lastSearchedZipCode) {
          setCachedZipCode(data.me?.lastSearchedZipCode);
        }
        dispatch(setAvailableAddOns({ availableAddOnItems: data.availableAddOnItems }));
      })
      .catch((err) => {
        globalReduce((state) => ({
          ...state,
          errors: [err.toString()],
          loaded: true,
        }));

        console.error(err);
      })
      .finally(() => {
        globalReduce((state) => ({ ...state, loading: false }));
        promiseResolve();
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalReduce, setCachedZipCode, cachedZipCode, userIsLoggedIn, GqlClient]);
  return fetchPromise;
};
